import React, { useCallback, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics/use-ai-events-instrumentation';
import { Images } from '@atlassian/smart-knowledge-cards/images';
import { ImagesSkeleton } from '@atlassian/smart-knowledge-cards/images-skeleton';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onNonGenerativeAIAnswerResultShown } from '../../../ai-answer-dialog/on-non-generative-ai-answer-result-shown';
import { type NonGenerativeAIAnswerResultShownProps } from '../../../ai-answer-dialog/types';
import { useSAINUsageCriteriaMet } from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { AnswerCardType } from '../../../constants';
import { isHello } from '../../../is-hello';
import { onNonGenerativeKnowledgeCardLinkClick } from '../../../knowledge-cards/on-non-generative-knowledge-card-link-click';
import { useOncePerKey } from '../../common/hooks/use-once-per-key';
import { useImages } from '../api/useImages/useImages';
import emptyStateSpotDark from '../assets/visual-knowledge-card-empty-dark.svg';
import emptyStateSpotLight from '../assets/visual-knowledge-card-empty-light.svg';
import errorStateSpotDark from '../assets/visual-knowledge-card-error-dark.svg';
import errorStateSpotLight from '../assets/visual-knowledge-card-error-light.svg';

// Shared by the <Images> prop and the analytics attribute so the two cannot drift.
export const VISUAL_MAX_VISIBLE_COUNT = 4;

const IMAGE_THUMBNAIL_LOCATION = 'imageThumbnail';
const IMAGE_SOURCE_LINK_LOCATION = 'imageSourceLink';
const IMAGE_CAROUSEL_ARROW_LOCATION = 'imageCarouselArrow';

const messages = defineMessages({
	heading: {
		id: 'search_ai.visual_knowledge_card.heading',
		defaultMessage: 'Images',
		description: 'Heading for the visual knowledge card showing images related to a search query.',
	},
	loadingLabel: {
		id: 'search_ai.visual_knowledge_card.loading_label',
		defaultMessage: 'Loading images',
		description:
			'Accessible label for the loading skeleton while related images are being fetched.',
	},
	statusBodyText: {
		id: 'search_ai.visual_knowledge_card.body_text',
		defaultMessage: 'Try searching again.',
		description: 'Body text in the visual knowledge card error or empty state, prompting retry.',
	},
	errorTitle: {
		id: 'search_ai.visual_knowledge_card.error_title',
		defaultMessage: "Something went wrong and we couldn't find related images.",
		description: 'Title shown in the visual knowledge card when an error occurs loading images.',
	},
	emptyTitle: {
		id: 'search_ai.visual_knowledge_card.empty_title',
		defaultMessage: "We couldn't find any images for this search.",
		description: 'Title shown in the visual knowledge card when no images match the search query.',
	},
});

const styles = cssMap({
	stateContainer: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
	},
	title: {
		color: token('color.text.subtlest'),
		font: token('font.body'),
	},
	body: {
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
	},
});

const VisualKnowledgeCardStatusMessage = ({
	heading,
	imageSrc,
	imageSrcDark,
	title,
	body,
}: {
	heading: string;
	imageSrc: string;
	imageSrcDark: string;
	title: string;
	body: string;
}): React.JSX.Element => (
	<Stack space="space.100">
		<Heading as="h2" size="xsmall">
			{heading}
		</Heading>
		<Box xcss={styles.stateContainer}>
			<Inline alignBlock="center" space="space.200">
				<Image src={imageSrc} srcDark={imageSrcDark} width={72} height={72} alt="" />
				<Stack space="space.050">
					<Box xcss={styles.title}>{title}</Box>
					<Box xcss={styles.body}>{body}</Box>
				</Stack>
			</Inline>
		</Box>
	</Stack>
);

export const VisualKnowledgeCard = ({
	onFMPReached,
}: {
	onFMPReached: () => void;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { commonAttributes, sourceProduct } = useAIAnswerContext();
	const { cloudId, query, queryHash, workspaceId } = commonAttributes;

	const { trackAIInteractionInit } = useAIEventsInstrumentation();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [fireAnalyticsEvent] = useAnalytics();

	const { images, loading, error } = useImages({
		searchQuery: query,
		cloudId,
		workspaceId,
		product: sourceProduct ?? '',
	});

	const buildAnalyticsAttributes = useCallback(
		(): NonGenerativeAIAnswerResultShownProps => ({
			searchSessionId: commonAttributes.searchSessionId,
			queryHash: commonAttributes.queryHash,
			brand: commonAttributes.brand,
			source: commonAttributes.source,
			query: commonAttributes.query,
			answerString: commonAttributes.answerString,
			// The card's own identity; the dialog-level commonAttributes.answerCardType is broader (SAIN).
			answerCardType: AnswerCardType.VISUAL,
			imageMaxVisibleCount: VISUAL_MAX_VISIBLE_COUNT,
			isHello: isHello(commonAttributes.cloudId),
		}),
		[commonAttributes],
	);

	const runImpressionOncePerQuery = useOncePerKey();
	const hasImages = !loading && !error && !!images && images.length > 0;
	useEffect(() => {
		if (!hasImages) {
			return;
		}
		runImpressionOncePerQuery(queryHash, () => {
			onFMPReached();
			trackAIInteractionInit();
			fireSAINUsageEvent({ commonAttributes });
			fireAnalyticsEvent(
				onNonGenerativeAIAnswerResultShown({
					...buildAnalyticsAttributes(),
					imageCount: images.length,
				}),
			);
		});
	}, [
		hasImages,
		queryHash,
		images,
		commonAttributes,
		buildAnalyticsAttributes,
		trackAIInteractionInit,
		fireSAINUsageEvent,
		fireAnalyticsEvent,
		runImpressionOncePerQuery,
		onFMPReached,
	]);

	const handleImageClick = useCallback(
		(index: number) => {
			fireSAINUsageEvent({ commonAttributes });
			fireAnalyticsEvent(
				onNonGenerativeKnowledgeCardLinkClick({
					...buildAnalyticsAttributes(),
					location: IMAGE_THUMBNAIL_LOCATION,
					imageIndex: index,
					imageCount: images?.length,
					imageSimilarity: images?.[index]?.similarity,
				}),
			);
		},
		[buildAnalyticsAttributes, commonAttributes, fireAnalyticsEvent, fireSAINUsageEvent, images],
	);

	const handleSourceLinkClick = useCallback(
		(index: number) => {
			fireSAINUsageEvent({ commonAttributes });
			fireAnalyticsEvent(
				onNonGenerativeKnowledgeCardLinkClick({
					...buildAnalyticsAttributes(),
					location: IMAGE_SOURCE_LINK_LOCATION,
					imageIndex: index,
					imageCount: images?.length,
					imageSimilarity: images?.[index]?.similarity,
				}),
			);
		},
		[buildAnalyticsAttributes, commonAttributes, fireAnalyticsEvent, fireSAINUsageEvent, images],
	);

	const handleCarouselArrowClick = useCallback(() => {
		fireAnalyticsEvent(
			onNonGenerativeKnowledgeCardLinkClick({
				...buildAnalyticsAttributes(),
				location: IMAGE_CAROUSEL_ARROW_LOCATION,
				imageCount: images?.length,
			}),
		);
	}, [buildAnalyticsAttributes, fireAnalyticsEvent, images]);

	if (loading) {
		return (
			<ImagesSkeleton
				heading={formatMessage(messages.heading)}
				aria-label={formatMessage(messages.loadingLabel)}
			/>
		);
	}

	if (error) {
		return (
			<VisualKnowledgeCardStatusMessage
				heading={formatMessage(messages.heading)}
				imageSrc={errorStateSpotLight}
				imageSrcDark={errorStateSpotDark}
				title={formatMessage(messages.errorTitle)}
				body={formatMessage(messages.statusBodyText)}
			/>
		);
	}

	if (!images || images.length === 0) {
		return (
			<VisualKnowledgeCardStatusMessage
				heading={formatMessage(messages.heading)}
				imageSrc={emptyStateSpotLight}
				imageSrcDark={emptyStateSpotDark}
				title={formatMessage(messages.emptyTitle)}
				body={formatMessage(messages.statusBodyText)}
			/>
		);
	}

	const mappedImages = images.map((image) => ({
		altText: image.altText,
		imageUrl: image.thumbnailUrl,
		sourceTitle: image.title,
		sourceUrl: image.url,
	}));

	return (
		<Images
			heading={formatMessage(messages.heading)}
			images={mappedImages}
			maxVisibleCount={VISUAL_MAX_VISIBLE_COUNT}
			onCarouselArrowClick={handleCarouselArrowClick}
			onImageClick={handleImageClick}
			onSourceLinkClick={handleSourceLinkClick}
		/>
	);
};
